import React from 'react';
import TeachEnglishPageLayout from '../components/TeachEnglishPageLayout';

const FlashcardDrills = () => {
  const title = 'Flashcard Drills';
  const image = '/Images/flashcards1.webp';
  const intro = 'Flashcards are the Swiss Army knife of vocabulary teaching: portable, adaptable, and backed by spaced-repetition science. This guide covers card design principles, drill routines, digital vs. physical, and ready-made sets.';

  const seoContent = [
    'Effective flashcards follow the \"one fact per card\" rule. Front: target item (word, phrase, image, sound). Back: meaning (translation, definition, L2 synonym, picture, example sentence). Avoid clutter—no full dictionary entries. For productive cards, flip the direction: meaning → form.',
    'Design for the skill you\'re training. Receptive (recognize meaning): L2 → L1/definition/image. Productive (recall form): L1/definition/image → L2. Pronunciation: add IPA + audio button. Collocation: \"make _____ decision\" → \"make a decision.\" Grammar: \"He _____ (go) yesterday\" → \"went.\" Contextualized cards beat isolated words for transfer.',
    'Drill routines that work: 1) Leitner Box (physical): 5 boxes, review schedule (Box 1 daily, Box 2 every 2 days, Box 3 weekly...). Correct → promote; incorrect → demote to Box 1. 2) Digital SRS (Anki, Quizlet): algorithm handles spacing. 3) Classroom \"Speed Rounds\": pairs, 60 seconds, how many correct? Rotate partners. 4) \"Taboo Cards\": describe without saying the word—builds paraphrase skill.',
    'Blend physical and digital. Physical: tactile, no screen, good for young learners, kinesthetic. Digital: audio, images, SRS algorithm, sync across devices, class sharing. Hybrid workflow: students create physical cards in class → photo/scan to digital deck for home review. Teacher curates master deck, shares via QR code.',
    'Gamify without gimmicks. Progress bars, streak counters, \"mastered\" badges tap into competence motivation. Team flashcard relays (first team to correctly define 20 cards wins) add social accountability. Keep competition low-stakes—celebrate improvement, not just speed.',
  ];

  const levels = [
    {
      level: 'design',
      label: 'Card Design Principles',
      tagline: 'Templates & examples',
      cards: [
        {
          title: 'Flashcard Template Pack',
          image: '/Images/grammar1.webp',
          desc: 'Printable PDFs: vocab (L2/L1), collocation, grammar, pronunciation, picture—4 sizes.',
          links: [{ text: 'Download', to: '/resources/flashcard-templates.pdf' }],
        },
        {
          title: 'Digital Card Creation Guide',
          image: '/Images/2people1.webp',
          desc: 'Anki: cloze, image occlusion, audio. Quizlet: rich text, diagrams, scan document.',
          links: [{ text: 'View Guide', to: '/resources/digital-card-guide.pdf' }],
        },
        {
          title: 'Contextualized Card Examples',
          image: '/Images/vocabulary.webp',
          desc: 'Before/after: isolated word → sentence gap → collocation → audio → personalization prompt.',
          links: [{ text: 'View Examples', to: '/flashcards/examples' }],
        },
      ],
    },
    {
      level: 'routines',
      label: 'Drill Routines',
      tagline: 'Classroom & self-study',
      cards: [
        {
          title: 'Leitner Box Starter Kit',
          image: '/Images/online-teacher.webp',
          desc: 'Printable box labels, tracking sheet, student contract, parent letter (for YL).',
          links: [{ text: 'Download Kit', to: '/resources/leitner-kit.pdf' }],
        },
        {
          title: 'Classroom Speed-Round Protocols',
          image: '/Images/tutor2-1280x470.webp',
          desc: '3 variations: Pair Race, Team Relay, Silent Sort—with timing, scoring, differentiation tips.',
          links: [{ text: 'View Protocols', to: '/flashcards/speed-rounds' }],
        },
        {
          title: 'Self-Study SRS Planner',
          image: '/Images/3people1a.webp',
          desc: 'Weekly planner: deck review goals, new card limits, weekend catch-up, progress graph.',
          links: [{ text: 'Download', to: '/resources/srs-planner.pdf' }],
        },
      ],
    },
    {
      level: 'sets',
      label: 'Ready-Made Flashcard Sets',
      tagline: 'Curated by level & topic',
      cards: [
        {
          title: 'Oxford 3000 / 5000 Decks',
          image: '/Images/flashcards1.webp',
          desc: 'CEFR-leveled, with IPA, audio, example sentence, and collocation tags.',
          links: [{ text: 'Access Decks', to: '/flashcards/oxford-3000' }],
        },
        {
          title: 'Phrasal Verb Master Set',
          image: '/Images/flashcards1.webp',
          desc: 'Top 150 phrasal verbs: particle meaning groups, context sentences, separable/inseparable.',
          links: [{ text: 'Access Deck', to: '/flashcards/phrasal-verbs' }],
        },
        {
          title: 'Academic Word List (AWL) Sublists',
          image: '/Images/flashcards1.webp',
          desc: '10 sublists × 60 words = 600 cards with derivation families and discipline tags.',
          links: [{ text: 'Access Decks', to: '/flashcards/awl' }],
        },
      ],
    },
  ];

  return (
    <TeachEnglishPageLayout
      title={title}
      image={image}
      intro={intro}
      seoContent={seoContent}
      levels={levels}
      seoTitle="Flashcard Drills: Design, Spaced Repetition Routines & Ready-Made Decks"
      seoDescription="Complete guide to flashcard-based vocabulary teaching. Card design principles (receptive/productive), Leitner box & digital SRS routines, classroom drill protocols, and free curated decks (Oxford 3000, Phrasal Verbs, AWL)."
    />
  );
};

export default FlashcardDrills;